import {
  CheckboxIndeterminate,
  CheckmarkFilled,
  CheckmarkFilledError,
  Edit,
  RetryFailed,
  TrashCan,
} from "@carbon/icons-react";
import {
  CurrencyDisplay,
  CopyButton,
  SwitchAppIconButton,
} from "../../../../../../components";
import { truncateStringWithEllipsis } from "../../../../../../utils";
import { ICreateBankTransfer } from "../../../../../../interfaces-and-types";
import { memo } from "react";

interface IBatchBankTransferEntryStructuredListItemProps {
  transfer: ICreateBankTransfer;
  index: number;
  isLoadingBatchListVerification: boolean;
  hasModifiedReference: boolean;
  batchListCount: number;
  removeBankTransferByIndex: (index: number) => void;
  toggleBankTransferBatchEntryEditMode: (index: number) => void;
  reVerifyTransfers: () => void;

  virtualizedListStyle?: any;
}

export const BatchBankTransferEntryStructuredListItem = memo(
  ({
    transfer,
    index,
    isLoadingBatchListVerification,
    hasModifiedReference,
    batchListCount,
    removeBankTransferByIndex,
    toggleBankTransferBatchEntryEditMode,
    reVerifyTransfers,
    virtualizedListStyle,
  }: IBatchBankTransferEntryStructuredListItemProps) => {
    return (
      <div
        // style={virtualizedListStyle}
        className={`cds--structured-list-row`}
        title={`Transfer ${index + 1} of ${batchListCount}${
          !transfer.description ? "" : `: ${transfer.description}`
        }`}
        key={transfer.reference}
      >
        <div className="cds--structured-list-td font-ctm--semibold text-primary">
          {index + 1}.
        </div>

        <div
          className="cds--structured-list-td"
          title={
            transfer.isLoadingResolveBeneficiary
              ? "Verifying Recipient..."
              : transfer.isBeneficiaryResolved
              ? "Verified Recipient"
              : "Recipient Could Not Be Verified"
          }
        >
          {transfer.isLoadingResolveBeneficiary ? (
            <CheckboxIndeterminate size="24" color="magenta" />
          ) : transfer.isBeneficiaryResolved ? (
            <CheckmarkFilled size="24" color="#00cf90fa" />
          ) : (
            <CheckmarkFilledError size="24" color="red" />
          )}
        </div>

        <div className="cds--structured-list-td">
          <CurrencyDisplay
            amount={transfer.amount!}
            currencyOrSymbol={transfer.currency}
            amountFontSize={16}
            symbolFontSize={12}
          />
        </div>

        <div className="cds--structured-list-td font-ctm--semibold">
          {transfer.beneficiary_id}
        </div>

        <div className="cds--structured-list-td font-ctm--semibold text-primary">
          {transfer.account_number}
        </div>

        <div className="cds--structured-list-td font-ctm--semibold text-teal">
          {transfer.accountNameHelper || transfer.account_name}
        </div>

        <div className="cds--structured-list-td font-ctm--semibold ">
          {transfer.bankNameHelper || transfer.bank_name}
        </div>

        <div
          className={`cds--structured-list-td ${
            hasModifiedReference ? "text-warning-2" : ""
          }`}
          title={transfer.reference}
        >
          <div className="flex-container justify-content--start align-items--center no-wrap">
            <div className="transaction-id-text modal-header--title">
              <span title={transfer.reference}>
                {truncateStringWithEllipsis(transfer.reference, 15)}
              </span>
            </div>
            <div className="copy-button--container ml-3">
              <CopyButton
                name="copy-reference-button"
                stringToCopy={transfer.reference}
                iconDescription="copy reference"
                className="copy-btn"
              />
            </div>
          </div>
        </div>

        <div className="cds--structured-list-td font-ctm--semibold">
          {transfer.beneficiaryEmail}
        </div>

        <div className="cds--structured-list-td">
          {isLoadingBatchListVerification ? null : (
            <SwitchAppIconButton
              size="sm"
              kind="tertiary"
              onClick={() => toggleBankTransferBatchEntryEditMode(index)}
              className="switch-edit-mode--button"
              title={"Edit transfer entry"}
            >
              <Edit
                className="text-primary"
                style={{ width: "30px", height: "30px" }}
              />
            </SwitchAppIconButton>
          )}

          {!transfer.isBeneficiaryResolved &&
          transfer.isErrorResolveBeneficiary ? (
            <SwitchAppIconButton
              size="sm"
              kind="danger"
              onClick={() => reVerifyTransfers(/*index*/)}
              className="delete-transfer--button"
              title="Re-verify transfer recipient"
            >
              <RetryFailed
                className="text-success ml-5"
                style={{ width: "30px", height: "30px" }}
              />
            </SwitchAppIconButton>
          ) : null}
        </div>

        <div className="cds--structured-list-td">
          {isLoadingBatchListVerification ? null : (
            <SwitchAppIconButton
              size="sm"
              kind="danger"
              onClick={() => removeBankTransferByIndex(index)}
              className="delete-transfer--button"
              title="Remove recipient from list"
            >
              <TrashCan
                className="text-danger"
                style={{ width: "30px", height: "30px" }}
              />
            </SwitchAppIconButton>
          )}
        </div>
      </div>
    );
  },
  (prevProps, nextProps) =>
    prevProps.index === nextProps.index &&
    prevProps.transfer.bank_code === nextProps.transfer.bank_code &&
    prevProps.transfer.account_number === nextProps.transfer.account_number &&
    prevProps.transfer.isBeneficiaryResolved ===
      nextProps.transfer.isBeneficiaryResolved &&
    prevProps.transfer.isErrorResolveBeneficiary ===
      nextProps.transfer.isErrorResolveBeneficiary
);
